import {
  AppError,
  isApiError,
  isNetworkError,
  isValidationError,
  isFirebaseError,
  createApiError,
  createNetworkError,
} from "../types/errors";

/**
 * Función utilitaria para manejo tipado de errores
 * Reemplaza el uso de catch (error: any)
 */
export const handleError = (error: unknown): AppError => {
  // Si ya es un error conocido, devolverlo
  if (
    isApiError(error) ||
    isNetworkError(error) ||
    isValidationError(error) ||
    isFirebaseError(error)
  ) {
    return error;
  }

  // Si es Error nativo de JavaScript
  if (error instanceof Error) {
    return error;
  }

  // Si es un error de Axios
  if (typeof error === "object" && error !== null && "response" in error) {
    const axiosError = error as any;
    if (axiosError.response) {
      return createApiError(
        axiosError.response.data?.message || "Error del servidor",
        axiosError.response.status,
        axiosError.response.data?.code
      );
    } else if (axiosError.request) {
      return createNetworkError("Error de conexión", 0);
    }
  }

  // Error desconocido - convertir a Error estándar
  return new Error(String(error));
};

/**
 * Hook para manejo de errores en componentes React
 * Uso: const { handleApiCall, handleFormSubmit } = useErrorHandler();
 */
export const useErrorHandler = () => {
  return {
    handleApiCall: async <T>(
      apiCall: () => Promise<T>
    ): Promise<{ data?: T; error?: AppError }> => {
      try {
        const data = await apiCall();
        return { data };
      } catch (error) {
        return { error: handleError(error) };
      }
    },

    handleFormSubmit: async <T>(
      submitFn: () => Promise<T>
    ): Promise<{ success: boolean; data?: T; error?: AppError }> => {
      try {
        const data = await submitFn();
        return { success: true, data };
      } catch (error) {
        const handledError = handleError(error);
        return { success: false, error: handledError };
      }
    },
  };
};

/**
 * Ejemplo de uso correcto para reemplazar catch(error: any)
 *
 * ANTES (❌ MAL):
 * try {
 *   await apiCall();
 * } catch (error: any) {
 *   console.error(error.message);
 * }
 *
 * DESPUÉS (✅ BIEN):
 * try {
 *   await apiCall();
 * } catch (error: unknown) {
 *   const handledError = handleError(error);
 *   console.error(handledError.message);
 * }
 */
export const exampleCorrectErrorHandling = async (
  id: string
): Promise<{ success: boolean; data?: any; error?: string }> => {
  try {
    const response = await fetch(`/api/data/${id}`);

    if (!response.ok) {
      throw createApiError(
        "Error al obtener datos",
        response.status,
        "FETCH_ERROR"
      );
    }

    const data = await response.json();
    return { success: true, data };
  } catch (error: unknown) {
    // ✅ CORRECTO: unknown en lugar de any
    const handledError = handleError(error);

    // Manejo específico por tipo de error
    if (isApiError(handledError)) {
      return {
        success: false,
        error: `Error de API: ${handledError.message} (${handledError.status})`,
      };
    }

    if (isNetworkError(handledError)) {
      return {
        success: false,
        error: "Error de conexión. Verifica tu internet.",
      };
    }

    if (isFirebaseError(handledError)) {
      return {
        success: false,
        error: `Error de Firebase: ${handledError.code}`,
      };
    }

    return {
      success: false,
      error: handledError.message,
    };
  }
};

/**
 * Wrapper para funciones async que pueden fallar
 * Elimina la necesidad de try/catch repetitivos
 */
export const safeAsync = async <T>(
  fn: () => Promise<T>
): Promise<{ data?: T; error?: AppError }> => {
  try {
    const data = await fn();
    return { data };
  } catch (error) {
    return { error: handleError(error) };
  }
};
